import { XTickHistoryEntry, xTickStore } from '../../ng-services/xtick.store';
import * as echarts from 'echarts/core';
import { EChartsType } from 'echarts/core';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    DataZoomComponent,
    MarkLineComponent,
    MarkPointComponent,
} from 'echarts/components';
import { CandlestickChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { BaseChartComponent } from '../chart/base-chart.component';
import { when } from 'mobx';
import moment from 'moment';

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LegendComponent,
    DataZoomComponent,
    MarkLineComponent,
    MarkPointComponent,
    CandlestickChart,
    LineChart,
    CanvasRenderer,
]);

// https://echarts.apache.org/examples/en/editor.html?c=candlestick-sh

const upColor = '#00da3c';
const upBorderColor = '#008F28';
const downColor = '#ec0000';
const downBorderColor = '#8A0000';

const INITIAL_CANDLE_NUM = 100;

type PreparedData = {
    dates: string[];
    values: [number, number, number, number][];
};

export interface ChartMarkerInterface {
    name: string;
    color: string;
    date: Date;
}

export interface ChartMarkLineInterface {
    name: string;
    color: string;
    yAxis: number;
}

function toUtfDateString(date: Date) {
    return date.toLocaleDateString(undefined, { timeZone: 'utc' });
}

class CandlestickChartComponent extends BaseChartComponent {
    symbol!: string;
    marker?: ChartMarkerInterface;
    markLines?: ChartMarkLineInterface[];

    async whenDataReady(): Promise<boolean> {
        await when(() => Boolean(this.history && this.history.length > 0));
        return true;
    }

    get isLoading() {
        return this.history === undefined;
    }

    get isHistoryEmpty() {
        return !this.isLoading && this.history && this.history?.length === 0;
    }

    protected prepareData(data: XTickHistoryEntry[]): PreparedData {
        const dates: string[] = [];
        const values: [number, number, number, number][] = [];

        data.forEach((entry) => {
            // open close low high
            values.push([entry.open, entry.close, entry.low, entry.high]);
            dates.push(toUtfDateString(new Date(entry.timestamp)));
        });

        // create a padding to the right of the candlesticks
        // this is so there is more space to the right of marker so the marker can fit
        if (data.length) {
            let date = moment(data[data.length - 1].timestamp);

            for (let i = 0; i < 6; i++) {
                date = date.clone().add(1, 'd');

                // add empty value
                values.push([] as any);
                dates.push(toUtfDateString(date.toDate()));
            }
        }

        return {
            dates,
            values,
        };
    }

    calculateMA(preparedData: PreparedData, dayCount: number) {
        const result = [];
        for (let i = 0, len = preparedData.values.length; i < len; i++) {
            if (i < dayCount) {
                result.push('-');
                continue;
            }
            let sum = 0;
            for (let j = 0; j < dayCount; j++) {
                sum += preparedData.values[i - j][1];
            }

            // handle NaN
            const ma = sum / dayCount;
            result.push(Number.isNaN(ma) ? '-' : ma.toFixed(2));
        }
        return result;
    }

    get history() {
        return xTickStore.getLongHistory(this.symbol);
    }

    $onInit() {
        super.$onInit();
        xTickStore.fetchLongHistory(this.symbol);
    }

    async initializeChart(chart: EChartsType) {
        await when(() => Boolean(this.history));

        if (!this.history || this.history?.length === 0) {
            return;
        }

        const preparedData = this.prepareData(this.history);

        chart.setOption({
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                },
                confine: true,
            },
            legend: {
                data: ['', 'MA5', 'MA10', 'MA8', 'MA20'],
            },
            grid: {
                left: 20,
                top: 20,
                right: 50,
                bottom: 80,
            },
            xAxis: {
                type: 'category',
                data: preparedData.dates,
                scale: true,
                boundaryGap: false,
                axisLine: { onZero: false },
                splitLine: { show: false },
                splitNumber: 20,
                min: 'dataMin',
                max: 'dataMax',
            },
            yAxis: {
                position: 'right',
                scale: true,
                splitArea: {
                    show: true,
                },
                min: (value: any) => {
                    if (!this.hasMarkLines) {
                        return value.min;
                    }

                    return Math.min(this.getMarkLineMin(), value.min);
                },
                max: (value: any) => {
                    if (!this.hasMarkLines) {
                        return value.max;
                    }

                    return Math.max(this.getMarkLineMax(), value.max);
                },
            },
            dataZoom: [
                {
                    type: 'inside',
                    // calculate start % so start <-> end is exactly INITIAL_CANDLE_NUM values
                    start: (100 - (INITIAL_CANDLE_NUM / this.history.length) * 100) | 0,
                    end: 100,
                },
                {
                    show: true,
                    type: 'slider',
                    top: '90%',
                    start: 0,
                    end: 100,
                },
            ],
            series: [
                {
                    type: 'candlestick',
                    data: preparedData.values,
                    itemStyle: {
                        color: upColor,
                        color0: downColor,
                        borderColor: upBorderColor,
                        borderColor0: downBorderColor,
                    },
                    markPoint: {
                        label: {
                            normal: {
                                formatter: function (param: any) {
                                    return param.name;
                                },
                            },
                        },
                        data: [
                            ...(this.marker
                                ? [
                                      {
                                          name: this.marker.name,
                                          coord: [
                                              toUtfDateString(this.marker.date),
                                              // find Y position for the marker
                                              this.history.find(
                                                  (d) =>
                                                      toUtfDateString(new Date(d.timestamp)) ===
                                                      toUtfDateString(this.marker!.date)
                                              )?.high ?? 0,
                                          ],
                                          itemStyle: {
                                              color: this.marker.color,
                                          },
                                      },
                                  ]
                                : []),
                        ],
                        tooltip: {
                            formatter: function (param: any) {
                                return param.name + '<br>' + (param.data.coord || '');
                            },
                        },
                    },
                    markLine: this.createMarkLines(),
                },
                {
                    name: 'MA5',
                    type: 'line',
                    data: this.calculateMA(preparedData, 5),
                    smooth: true,
                    lineStyle: {
                        opacity: 0.5,
                    },
                    symbol: 'none',
                },
                {
                    name: 'MA10',
                    type: 'line',
                    data: this.calculateMA(preparedData, 10),
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        opacity: 0.5,
                    },
                },
                {
                    name: 'MA8',
                    type: 'line',
                    data: this.calculateMA(preparedData, 8),
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        opacity: 0.5,
                    },
                },
                {
                    name: 'MA20',
                    type: 'line',
                    data: this.calculateMA(preparedData, 20),
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        opacity: 0.5,
                    },
                },
            ],
        });
    }

    get hasMarkLines() {
        return Boolean(this.markLines && this.markLines?.length > 0);
    }

    getMarkLineMax() {
        const values = (this.markLines || []).map((m) => m.yAxis);

        return Math.max(...values);
    }

    getMarkLineMin() {
        const values = (this.markLines || []).map((m) => m.yAxis);
        Math.min();

        return Math.min(...values);
    }

    createMarkLines() {
        const markLines = this.markLines || [];

        return {
            symbol: ['none', 'none'],
            data: markLines.map(({ name, color, yAxis }) => ({
                name,
                yAxis,
                x: '70%',
                label: {
                    position: 'start',
                    formatter: '{b}',
                    show: true,
                    color: '#333',
                    backgroundColor: '#fff',
                    padding: 5,
                },
                lineStyle: {
                    color,
                    width: 3,
                    type: 'solid',
                },
            })),
        };
    }
}

angular.module('tradersyard').component('tyCandlestickChart', {
    // language=HTML
    template: `
        <div class="chart">
            <div class="chart__target target_chart_element"></div>
            <div class="chart_no_data" ng-if="$ctrl.isHistoryEmpty">No data available.</div>
        </div>
    `,
    controller: CandlestickChartComponent,
    bindings: {
        symbol: '<',
        marker: '<',
        markLines: '<',
    },
});
